import type { ListInboundsInput } from "@nexxt/contracts/procurement";
import { infiniteQueryOptions, queryOptions } from "@tanstack/react-query";
import { procurementClient } from "../client";

// Server state of the inbounds (50-api/procurement.md §2.6): one factory, the same keys for loaders, screens and
// mutations. Notes, history and what only Tomorrow reads of an inbound keep their keys under `of`, so whatever reloads
// the inbounds reloads them too; the bytes of a document do not change, and stay out of it.

/** The filters of the list input (`listInboundsInput`), without the paging the queries add. */
export type InboundFilters = Pick<ListInboundsInput, "status" | "brand" | "supplier" | "q"> & {
  toCapture?: boolean | undefined;
  /** ADR-233: the lines the extraction tool read wait for Logistics; Tomorrow's only. */
  extractionToReview?: boolean | undefined;
  toResolve?: boolean | undefined;
  toSettle?: boolean | undefined;
  /** What the supplier left undone after the last reminder and a cadence (B-41, B-42, ADR-245): Logistics' queue. */
  remindersOver?: boolean | undefined;
  asSupplier?: boolean | undefined;
  asBrand?: boolean | undefined;
  /** The sets of the app's queues "To do", as a JSON array (ADR-302). */
  anyOf?: string | undefined;
};

const PAGE_SIZE = 50;

const keys = {
  all: ["inbounds"] as const,
  /** Prefix of every cached list: a write puts the inbound it gets back in each of them. */
  lists: ["inbounds", "list"] as const,
  /** Prefix of every cached detail: by id or by number, as the URL names it. */
  details: ["inbounds", "detail"] as const,
  /** Prefix of what is cached of one inbound besides its detail: notes, history, and the app's. */
  of: (inboundId: string) => ["inbounds", "of", inboundId] as const,
};

export const inboundQueries = {
  ...keys,

  list: (filters: InboundFilters) =>
    infiniteQueryOptions({
      queryKey: [...keys.lists, filters],
      queryFn: ({ pageParam, signal }) =>
        procurementClient().listInbounds({ ...filters, page: pageParam, pageSize: PAGE_SIZE }, signal),
      initialPageParam: 1,
      getNextPageParam: (last) => (last.hasMore ? last.page + 1 : undefined),
    }),

  /**
   * The open lines of a confirmed PO an inbound can carry, with what the inbounds sent announced and the tolerance of
   * each (V-02): what a new inbound and the lines of a draft start from. Under the inbounds: a submission changes them.
   */
  candidates: (purchaseOrderId: string) =>
    queryOptions({
      queryKey: [...keys.all, "candidates", purchaseOrderId],
      queryFn: ({ signal }) => procurementClient().listInboundCandidates({ purchaseOrderId }, signal),
    }),

  /** By id or by number, with its lines, documents, packing list and last check. */
  detail: (key: string) =>
    queryOptions({
      queryKey: [...keys.details, key],
      queryFn: ({ signal }) => procurementClient().getInbound({ key }, signal),
    }),

  history: (inboundId: string) =>
    infiniteQueryOptions({
      queryKey: [...keys.of(inboundId), "history"],
      queryFn: ({ pageParam, signal }) =>
        procurementClient().getInboundHistory({ inboundId, ...(pageParam && { cursor: pageParam }) }, signal),
      initialPageParam: "",
      getNextPageParam: (last) => last.nextCursor ?? undefined,
    }),

  /**
   * The bytes of a document, for its preview (ADR-132): a file does not change, so they are read once, under a key no
   * write of the inbound reloads: the PDF Logistics is reading stays where it is when the packing list is saved.
   */
  documentContent: (inboundId: string, documentId: string) =>
    queryOptions({
      queryKey: ["inbound-documents", inboundId, documentId],
      queryFn: ({ signal }) => procurementClient().downloadInboundDocument({ inboundId, documentId }, signal),
      staleTime: Number.POSITIVE_INFINITY,
      // a PDF is a few MB: it is not kept once no screen shows it
      gcTime: 60_000,
    }),
};

/** Where the browser downloads a document of an inbound, as an attachment (ADR-069, ADR-128). */
export const inboundDocumentHref = (inboundId: string, documentId: string): string =>
  procurementClient().inboundDocumentUrl({ inboundId, documentId });
